import useInertiaData from '@/hooks/useInertiaData';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeType = 'cblocks' | 'cfinance';

interface ThemeContextType {
    theme: ThemeType;
    setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const { user } = useInertiaData();
    const [theme, setThemeState] = useState<ThemeType>('cblocks');

    useEffect(() => {
        // Determine theme based on current academy's theme field
        if (user?.current_academy?.theme) {
            setThemeState(user.current_academy.theme as ThemeType);
        } else {
            // Fallback to default theme if no theme is set
            setThemeState('cblocks');
        }
    }, [user?.current_academy]);

    useEffect(() => {
        // Apply theme class to document body
        const body = document.body;
        body.classList.remove('cblocks', 'cfinance');
        body.classList.add(theme);
    }, [theme]);

    const setTheme = (newTheme: ThemeType) => {
        setThemeState(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
